'use client';

import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import { CheckCircle, Question, WarningCircle, XCircle } from '@phosphor-icons/react/dist/ssr';
import { cva } from 'class-variance-authority';
import Link from 'next/link';
import type { ReactNode, RefObject } from 'react';
import { Button } from './button';
type Variant = 'info' | 'warning' | 'destructive' | 'success';
const ICON_COMPONENTS: Record<Variant, Icon> = {
  info: Question,
  warning: WarningCircle,
  destructive: XCircle,
  success: CheckCircle
};
const alertVariants = cva('relative w-full rounded-[8px] p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4', {
  variants: {
    variant: {
      info: 'bg-[#a3426c]/10 text-[#a3426c] [&>svg]:text-[#a3426c]',
      warning: 'bg-[#fbbf24]/25 text-[#f78748] [&>svg]:text-[#f78748]',
      success: 'bg-[#4fc36e]/20 text-[#29b54e] [&>svg]:text-[#29b54e]',
      destructive: 'bg-[#ff175c]/10 text-[#e11c56] [&>svg]:text-[#e11c56]'
    }
  },
  defaultVariants: {
    variant: 'info'
  }
});
export default function Alert({
  variant,
  title,
  description,
  className,
  buttons,
  ref,
  icon,
  sendFeedbackButton = false,
  onDismiss
}: {
  variant: Variant;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  buttons?: ReactNode | ReactNode[];
  ref?: RefObject<HTMLDivElement | null>;
  icon?: ReactNode;
  sendFeedbackButton?: boolean;
  onDismiss?: () => void;
}) {
  const IconComponent = ICON_COMPONENTS[variant];
  const showButtons = buttons || sendFeedbackButton || onDismiss;
  return <div ref={ref} role="alert" className={cn(alertVariants({
    variant
  }), className)} data-sentry-component="Alert" data-sentry-source-file="alert.tsx">
      <h5 className="flex items-center text-lg font-medium font-sourcesans3 leading-none">
        <span>{icon || <IconComponent weight="fill" className="h-6 w-6 inline-block mr-1" />}</span>
        {title}
      </h5>

      {description && <div className="[&_p]:leading-relaxed mt-1 font-sourcesans3 text-burgundy">{description}</div>}

      {showButtons && <div className="flex mt-4 flex-col gap-2 items-end">
          {buttons}

          {sendFeedbackButton && <Button size="small" asChild>
              <Link target="_blank" href="https://sensay.canny.io/bugs">
                Send feedback
              </Link>
            </Button>}

          {onDismiss && <Button onClick={onDismiss} size={'small'}>
              Dismiss
            </Button>}
        </div>}
    </div>;
}